import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import { useThemeStore, themes, type Theme } from '@/stores/theme-store';
import { useI18n } from '@/lib/i18n';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export function ThemeSelector() {
    const { theme, setTheme, interactiveEnabled, setInteractiveEnabled } = useThemeStore();
    const { language, t } = useI18n();

    const handleThemeSelect = async (selectedTheme: Theme) => {
        setTheme(selectedTheme);

        // Update theme on server
        try {
            const API_URL = import.meta.env.VITE_API_URL || '/api';
            await fetch(`${API_URL}/auth/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ theme: selectedTheme }),
            });
        } catch (error) {
            console.error('Failed to save theme preference to server:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Theme Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {themes.map((themeConfig) => {
                    const isSelected = theme === themeConfig.id;
                    const name = language === 'fr' ? themeConfig.nameFr : themeConfig.nameEn;
                    const description = language === 'fr' ? themeConfig.descriptionFr : themeConfig.description;

                    return (
                        <button
                            key={themeConfig.id}
                            onClick={() => handleThemeSelect(themeConfig.id)}
                            className={cn(
                                'relative group flex flex-col rounded-xl overflow-hidden border-2 transition-all duration-200',
                                isSelected
                                    ? 'border-primary ring-2 ring-primary/20'
                                    : 'border-white/10 hover:border-primary/50'
                            )}
                        >
                            {/* Theme Preview */}
                            <div
                                className="h-20 w-full relative"
                                style={{ background: themeConfig.preview }}
                            >
                                {/* Interactive indicator */}
                                {themeConfig.interactive && (
                                    <div className="absolute top-1.5 right-1.5">
                                        <Sparkles className="h-3.5 w-3.5 text-white/70" />
                                    </div>
                                )}

                                {/* Selected checkmark */}
                                {isSelected && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                                            <Check className="h-5 w-5 text-white" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Theme Info */}
                            <div className="p-2.5 backdrop-blur-md bg-white/5 text-left">
                                <p className="font-medium text-sm text-white truncate">
                                    {name}
                                </p>
                                <p className="text-xs text-white/50 truncate">
                                    {description}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Interactive Effects Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg backdrop-blur-md bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <Label className="text-sm font-medium text-white">
                            {t('settings.interactiveEffects')}
                        </Label>
                        <p className="text-xs text-white/50">
                            {t('settings.interactiveEffectsDesc')}
                        </p>
                    </div>
                </div>
                <Switch
                    checked={interactiveEnabled}
                    onCheckedChange={setInteractiveEnabled}
                />
            </div>
        </div>
    );
}
